import { useTranslate } from '@ur-solutions/react-hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { PermissionDomain } from './types'

export function usePermissionDomainTranslations() {
  return useTranslate({
    absence: 'common.absence',
    approve: 'permissions.approve',
    checkInOut: 'permissions.check-in-out',
    company: 'common.company',
    companySettings: 'settings.company-settings',
    documents: 'common.documents',
    driverCard: 'users.driver-card',
    driverdataAll: 'user-types.everyones-driver-activities',
    driverdataSelf: 'user-types.driver-activities-self',
    dropdowns: 'common.dropdowns',
    exports: 'exports.export-data',
    formcreator: 'common.formcreator',
    formResponses: 'common.form-responses',
    forum: 'common.forum',
    forumChangeSticky: 'permissions.change_sticky',
    forumPosts: 'common.forum-posts',
    forumSendEmail: 'permissions.forum-send-email',
    forumSendSms: 'permissions.forum-send-sms',
    forumThreads: 'common.forum-threads',
    general: 'permissions.company-settings-general',
    handbook: 'common.handbook',
    infringements: 'common.infringements',
    issues: 'common.issues',
    routePlanner: 'common.route-planner',
    sms: 'common.sms',
    tableSettings: 'settings.tables',
    terminal: 'common.terminal',
    timesheets: 'common.hourlist',
    userDocuments: 'common.documents',
    userEquipment: 'common.user-equipment',
    users: 'common.users',
    userChild: 'common.user-child',
    userTypes: 'common.user-types',
    vehicles: 'vehicles.fleet-overview',
    vehiclesNotes: 'vehicles.note-permissions',
    vehiclesDocuments: 'vehicles.vehicle-documents',
    vehiclesExpirations: 'vehicles.vehicle-expirations',
    fleet: 'vehicles.fleet',
    workPeriods: 'salary.work-periods',

    viewAbsencePeriodReport: 'permissions.view-absence-period-report',
    viewAbsencePeriodReportOther:
      'permissions.view-absence-period-report-other',
    manageOwnAbsenceBalance: 'permissions.manage-own-absence-balance',
    manageOthersAbsenceBalance: 'permissions.manage-others-absence-balance',
    manageAbsenceLocks: 'permissions.manage-absence-locks',

    view: 'permissions.view',
    add: 'permissions.add',
    change: 'permissions.change',
    remove: 'permissions.remove',

    viewChange: 'permissions.view-and-change',
    overrideSalaryCode: 'permissions.override-salary-code',
    salary: 'salary.salary-bases',
    salaryAddons: 'salary.time-addons',
    salaryOverrides: 'salary.salary-code-overrides',
    dayAddons: 'salary.fixed-addons',

    viewOwn: 'permissions.view-own',
    addOwn: 'permissions.add-own',
    changeOwn: 'permissions.change-own',
    removeOwn: 'permissions.remove-own',

    viewOthers: 'permissions.view-others',
    addOthers: 'permissions.add-others',
    changeOthers: 'permissions.change-others',
    removeOthers: 'permissions.remove-others',

    viewSalaryMode: 'permissions.view-salary-mode',
    changeSalaryMode: 'permissions.change-salary-mode',

    changeActivityPaidStatus: 'permissions.change-activity-paid-status',
    diet: 'common.diet',

    planner: {
      planner: 'common.planner',
      access: 'permissions.planner-access',
      viewUnownedItems: 'permissions.planner-view-unowned-items',
      manageOwnedItems: 'permissions.planner-manage-owned-items',
      manageUnownedItems: 'permissions.planner-manage-unowned-items',
      useTemplates: 'permissions.planner-use-templates',
      createTemplates: 'permissions.planner-create-templates',
      deleteTemplates: 'permissions.planner-delete-templates',
    },
  })
}

export function createPermissionDomains(
  translations: ReturnType<typeof usePermissionDomainTranslations>
): PermissionDomain[] {
  const {
    view,
    add,
    change,
    remove,
    viewChange,
    viewOwn,
    addOwn,
    changeOwn,
    removeOwn,
    viewOthers,
    addOthers,
    changeOthers,
    removeOthers,
    approve,
    checkInOut,
    overrideSalaryCode,

    viewAbsencePeriodReport,
    viewAbsencePeriodReportOther,
    manageOwnAbsenceBalance,
    manageOthersAbsenceBalance,
    manageAbsenceLocks,

    viewSalaryMode,
    changeSalaryMode,
  } = translations

  return [
    {
      title: translations.company,
      permissions: {
        [view]: [PERMISSIONS.companies.view.company],
        [translations.exports]: [PERMISSIONS.companies.export.exportReports],
      },
    },
    {
      title: translations.users,
      permissions: {
        [view]: [PERMISSIONS.users.view.user],
        [add]: [PERMISSIONS.users.add.user],
        [change]: [PERMISSIONS.users.change.user],
        [remove]: [PERMISSIONS.users.delete.user],
      },
    },
    {
      title: translations.documents,
      permissions: {
        [view]: [PERMISSIONS.users.view.userdocument],
        [add]: [PERMISSIONS.users.add.userdocument],
        [change]: [PERMISSIONS.users.change.userdocument],
        [remove]: [PERMISSIONS.users.delete.userdocument],
      },
    },
    {
      title: translations.userEquipment,
      permissions: {
        [view]: [
          PERMISSIONS.users.view.userequipment,
          PERMISSIONS.users.view.userequipmenttype,
        ],
        [add]: [
          PERMISSIONS.users.add.userequipment,
          PERMISSIONS.users.add.userequipmenttype,
        ],
        [change]: [
          PERMISSIONS.users.change.userequipment,
          PERMISSIONS.users.change.userequipmenttype,
        ],
        [remove]: [
          PERMISSIONS.users.delete.userequipment,
          PERMISSIONS.users.delete.userequipmenttype,
        ],
      },
    },
    {
      title: translations.userChild,
      permissions: {
        [viewOwn]: [PERMISSIONS.users.view.userchild],
        [addOwn]: [PERMISSIONS.users.add.userchild],
        [changeOwn]: [PERMISSIONS.users.change.userchild],
        [removeOwn]: [PERMISSIONS.users.delete.userchild],
        [viewOthers]: [PERMISSIONS.users.view.userchildother],
        [addOthers]: [PERMISSIONS.users.add.userchildother],
        [changeOthers]: [PERMISSIONS.users.change.userchildother],
        [removeOthers]: [PERMISSIONS.users.delete.userchildother],
      },
    },
    {
      title: translations.forum,
      permissions: {
        [translations.forumSendSms]: [PERMISSIONS.forum.send.thread_sms],
        [translations.forumSendEmail]: [PERMISSIONS.forum.send.thread_email],
        [translations.forumChangeSticky]: [PERMISSIONS.forum.change.sticky],
      },
    },
    {
      title: translations.forumThreads,
      permissions: {
        [view]: [
          PERMISSIONS.forum.view.thread,
          PERMISSIONS.forum.view.threadattachment,
        ],
        [add]: [
          PERMISSIONS.forum.add.thread,
          PERMISSIONS.forum.add.threadattachment,
        ],
        [change]: [
          PERMISSIONS.forum.change.thread,
          PERMISSIONS.forum.change.threadattachment,
          PERMISSIONS.forum.delete.thread,
        ],
      },
    },
    {
      title: translations.forumPosts,
      permissions: {
        [view]: [
          PERMISSIONS.forum.view.post,
          PERMISSIONS.forum.view.postattachment,
        ],
        [add]: [
          PERMISSIONS.forum.add.post,
          PERMISSIONS.forum.add.postattachment,
        ],
        [change]: [
          PERMISSIONS.forum.change.post,
          PERMISSIONS.forum.change.postattachment,

          PERMISSIONS.forum.delete.post,
          PERMISSIONS.forum.delete.postattachment,
        ],
      },
    },
    {
      title: translations.driverCard,
      permissions: {
        [view]: [PERMISSIONS.users.view.driver_card],
        [add]: [PERMISSIONS.users.add.driver_card],
      },
    },
    {
      title: translations.driverdataSelf,
      permissions: {
        [view]: [PERMISSIONS.activities.view.driveractivity],
        [approve]: [PERMISSIONS.activities.approve.driveractivity],
      },
    },
    {
      title: translations.diet,
      permissions: {
        [change]: [PERMISSIONS.activities.change.driverdiet],
      },
    },
    {
      title: translations.driverdataAll,
      permissions: {
        [viewChange]: [PERMISSIONS.activities.change.driveractivity],
        [approve]: [PERMISSIONS.activities.approve.othersdriveractivity],
        [translations.changeActivityPaidStatus]: [
          PERMISSIONS.activities.change.driveractivitypaidstatus,
        ],
      },
    },
    {
      title: translations.terminal,
      permissions: {
        [view]: [
          PERMISSIONS.terminal.view.terminal,
          PERMISSIONS.terminal.view.terminalentry,
        ],
        [add]: [PERMISSIONS.terminal.add.terminalentry],
        [change]: [PERMISSIONS.terminal.change.terminalentry],
        [remove]: [PERMISSIONS.terminal.delete.terminalentry],
      },
    },
    {
      title: translations.infringements,
      permissions: {
        [view]: [PERMISSIONS.infringements.view.infringement],
        [viewOthers]: [PERMISSIONS.infringements.view.othersinfringement],
        [add]: [PERMISSIONS.infringements.add.infringement],
        [change]: [PERMISSIONS.infringements.change.infringement],
        [remove]: [PERMISSIONS.infringements.delete.infringement],
      },
    },
    {
      title: translations.issues,
      permissions: {
        [view]: [PERMISSIONS.issues.view.issue],
        [viewOthers]: [PERMISSIONS.issues.view.othersissue],
        [add]: [PERMISSIONS.issues.add.issue],
        [change]: [PERMISSIONS.issues.change.issue],
        [remove]: [PERMISSIONS.issues.delete.issue],
      },
    },
    {
      title: translations.timesheets,
      permissions: {
        [view]: [PERMISSIONS.timesheets.view.timeentry],
        [add]: [PERMISSIONS.timesheets.add.timeentry],
        [change]: [PERMISSIONS.timesheets.change.timeentry],
        [remove]: [PERMISSIONS.timesheets.delete.timeentry],
        [viewOthers]: [PERMISSIONS.timesheets.view.otherstimeentry],
        [overrideSalaryCode]: [
          PERMISSIONS.timesheets.overrideTimeEntrySalaryCode,
        ],
        [checkInOut]: [
          PERMISSIONS.timesheets.add.checkinout,
          PERMISSIONS.timesheets.change.checkinout,
        ],
      },
    },
    {
      title: translations.absence,
      permissions: {
        [view]: [PERMISSIONS.timesheets.view.absence],
        [add]: [PERMISSIONS.timesheets.add.absence],
        [change]: [PERMISSIONS.timesheets.change.absence],
        [remove]: [PERMISSIONS.timesheets.delete.absence],
        [viewOthers]: [PERMISSIONS.timesheets.view.othersabsence],
        [addOthers]: [PERMISSIONS.timesheets.add.othersabsence],
        [changeOthers]: [PERMISSIONS.timesheets.change.othersabsence],
        [removeOthers]: [PERMISSIONS.timesheets.delete.othersabsence],
        [viewAbsencePeriodReport]: [
          PERMISSIONS.timesheets.view.absenceperiodreport,
        ],
        [viewAbsencePeriodReportOther]: [
          PERMISSIONS.timesheets.view.absenceperiodreportother,
        ],
        [manageOwnAbsenceBalance]: [
          PERMISSIONS.timesheets.add.absencehourtransaction,
          PERMISSIONS.timesheets.change.absencehourtransaction,
          PERMISSIONS.timesheets.delete.absencehourtransaction,
        ],
        [manageOthersAbsenceBalance]: [
          PERMISSIONS.timesheets.add.absencehourtransactionother,
        ],
        [manageAbsenceLocks]: [
          PERMISSIONS.timesheets.view.absencelockconfig,
          PERMISSIONS.timesheets.add.absencelockconfig,
          PERMISSIONS.timesheets.change.absencelockconfig,
          PERMISSIONS.timesheets.delete.absencelockconfig,
        ],
      },
    },
    {
      title: translations.handbook,
      permissions: {
        [view]: [
          PERMISSIONS.handbook.view.manualfolder,
          PERMISSIONS.handbook.view.manualentry,
          PERMISSIONS.handbook.view.manualentryattachment,
        ],
        [add]: [
          PERMISSIONS.handbook.add.manualfolder,
          PERMISSIONS.handbook.add.manualentry,
          PERMISSIONS.handbook.add.manualentryattachment,
        ],
        [change]: [
          PERMISSIONS.handbook.change.manualfolder,
          PERMISSIONS.handbook.change.manualentry,
          PERMISSIONS.handbook.change.manualentryattachment,
        ],
      },
    },
    {
      title: translations.formcreator,
      permissions: {
        [view]: [PERMISSIONS.formcreator.view.formtemplate],
        [add]: [
          PERMISSIONS.formcreator.add.filledoutform,
          PERMISSIONS.formcreator.add.formtemplate,
        ],
        [change]: [PERMISSIONS.formcreator.change.formtemplate],
        [remove]: [
          PERMISSIONS.formcreator.delete.filledoutform,
          PERMISSIONS.formcreator.delete.formtemplate,
        ],
      },
    },
    {
      title: translations.formResponses,
      permissions: {
        [view]: [PERMISSIONS.formcreator.view.filledoutform],
        [viewOthers]: [PERMISSIONS.formcreator.change.filledoutform],
      },
    },
    {
      title: translations.vehicles,
      permissions: {
        [view]: [
          PERMISSIONS.vehicles.view.vehicle,
          PERMISSIONS.vehicles.view.vehiclefetchinfo,
          PERMISSIONS.vehicles.view.vehiclenote,
        ],
        [add]: [
          PERMISSIONS.vehicles.add.vehicle,
          PERMISSIONS.vehicles.add.vehiclefetchinfo,
        ],
        [change]: [
          PERMISSIONS.vehicles.change.vehicle,
          PERMISSIONS.vehicles.change.vehiclefetchinfo,
        ],
        [remove]: [
          PERMISSIONS.vehicles.delete.vehicle,
          PERMISSIONS.vehicles.delete.vehiclefetchinfo,
        ],
      },
    },
    {
      title: translations.vehiclesNotes,
      permissions: {
        [add]: [PERMISSIONS.vehicles.add.vehiclenote],
        [change]: [PERMISSIONS.vehicles.change.vehiclenote],
        [remove]: [PERMISSIONS.vehicles.delete.vehiclenote],
      },
    },
    {
      title: translations.vehiclesDocuments,
      permissions: {
        [view]: [PERMISSIONS.vehicles.view.vehicledocument],
        [add]: [PERMISSIONS.vehicles.add.vehicledocument],
        [change]: [PERMISSIONS.vehicles.change.vehicledocument],
        [remove]: [PERMISSIONS.vehicles.delete.vehicledocument],
      },
    },
    {
      title: translations.vehiclesExpirations,
      permissions: {
        [view]: [PERMISSIONS.vehicles.view.vehicleexpiration],
        [add]: [PERMISSIONS.vehicles.add.vehicleexpiration],
        [change]: [PERMISSIONS.vehicles.change.vehicleexpiration],
        [remove]: [PERMISSIONS.vehicles.delete.vehicleexpiration],
      },
    },
    {
      title: translations.fleet,
      permissions: {
        [view]: [PERMISSIONS.fleet.view.vehiclegroup],
      },
    },
    {
      title: translations.salary,
      permissions: {
        [view]: [
          PERMISSIONS.salary.view.salarybase,
          PERMISSIONS.salary.view.salarybaseentry,
        ],
        [add]: [
          PERMISSIONS.salary.add.salarybase,
          PERMISSIONS.salary.add.salarybaseentry,
        ],
        [change]: [
          PERMISSIONS.salary.change.salarybase,
          PERMISSIONS.salary.change.salarybaseentry,
        ],
        [remove]: [
          PERMISSIONS.salary.delete.salarybase,
          PERMISSIONS.salary.delete.salarybaseentry,
        ],
      },
    },
    {
      title: translations.workPeriods,
      permissions: {
        [view]: [PERMISSIONS.workPeriod.view.workperiod],
        [viewOthers]: [PERMISSIONS.workPeriod.view.othersworkperiod],
        [viewSalaryMode]: [PERMISSIONS.salary.view.salaryMode],
        [changeSalaryMode]: [PERMISSIONS.salary.change.salarySetting],
        [approve]: [
          PERMISSIONS.salary.approve.workperiod,
          PERMISSIONS.workPeriod.approve.workperiod,
          PERMISSIONS.workPeriod.change.workperiod,
        ],
      },
    },
    {
      title: translations.salaryAddons,
      permissions: {
        [view]: [PERMISSIONS.salary.view.timeaddon],
        [add]: [PERMISSIONS.salary.add.timeaddon],
        [change]: [PERMISSIONS.salary.change.timeaddon],
        [remove]: [PERMISSIONS.salary.delete.timeaddon],
      },
    },
    {
      title: translations.salaryOverrides,
      permissions: {
        [view]: [PERMISSIONS.salary.view.timeoverride],
        [add]: [PERMISSIONS.salary.add.timeoverride],
        [change]: [PERMISSIONS.salary.change.timeoverride],
        [remove]: [PERMISSIONS.salary.delete.timeoverride],
      },
    },

    {
      title: translations.dayAddons,
      permissions: {
        [view]: [PERMISSIONS.salary.view.fixedaddon],
        [add]: [PERMISSIONS.salary.add.fixedaddon],
        [change]: [PERMISSIONS.salary.change.fixedaddon],
        [remove]: [PERMISSIONS.salary.delete.fixedaddon],
      },
    },
    {
      title: translations.routePlanner,
      permissions: {
        [view]: [
          PERMISSIONS.routePlanner.view.routePlan,
          PERMISSIONS.routePlanner.view.routePlanPoint,
          PERMISSIONS.routePlanner.view.routePlanInstruction,
          PERMISSIONS.routePlanner.view.routePlanNotice,
          PERMISSIONS.routePlanner.view.routePlanNoticeDetail,
          PERMISSIONS.routePlanner.view.routeRestriction,
          PERMISSIONS.routePlanner.view.routeWeightRestriction,
        ],
        [add]: [
          PERMISSIONS.routePlanner.add.routePlan,
          PERMISSIONS.routePlanner.add.routeRestriction,
          PERMISSIONS.routePlanner.add.routeWeightRestriction,
        ],
        [change]: [PERMISSIONS.routePlanner.change.routePlan],
        [remove]: [PERMISSIONS.routePlanner.delete.routePlan],
      },
    },
    {
      title: translations.planner.planner,
      permissions: {
        [translations.planner.access]: [
          PERMISSIONS.planner.view.calendaritem,
          PERMISSIONS.planner.view.calendaritemcomment,
          PERMISSIONS.planner.add.calendaritemcomment,
          PERMISSIONS.planner.change.calendaritemcomment,
        ],
        [translations.planner.manageOwnedItems]: [
          PERMISSIONS.planner.add.calendaritem,
          PERMISSIONS.planner.change.calendaritem,
          PERMISSIONS.planner.delete.calendaritem,
        ],
        [translations.planner.viewUnownedItems]: [
          PERMISSIONS.planner.view.unownedcalendaritem,
        ],
        [translations.planner.manageUnownedItems]: [
          PERMISSIONS.planner.add.unownedcalendaritem,
          PERMISSIONS.planner.change.unownedcalendaritem,
          PERMISSIONS.planner.delete.unownedcalendaritem,
        ],
        [translations.planner.useTemplates]: [
          PERMISSIONS.planner.view.calendarscheduledynamictemplate,
          PERMISSIONS.planner.view.calendarschedulestatictemplate,
        ],
        [translations.planner.createTemplates]: [
          PERMISSIONS.planner.add.calendarscheduledynamictemplate,
          PERMISSIONS.planner.add.calendarschedulestatictemplate,
        ],
        [translations.planner.deleteTemplates]: [
          PERMISSIONS.planner.delete.calendarscheduledynamictemplate,
          PERMISSIONS.planner.delete.calendarschedulestatictemplate,
        ],
      },
    },
    {
      title: translations.companySettings,
      permissions: {
        [view]: [PERMISSIONS.companies.view.companysetting],
        [change]: [PERMISSIONS.companies.change.companysetting],
        [translations.general]: [
          PERMISSIONS.companies.change.company_general_settings,
        ],
        [translations.absence]: [
          PERMISSIONS.timesheets.view.absencetype,
          PERMISSIONS.timesheets.add.absencetype,
        ],
        [translations.issues]: [
          PERMISSIONS.issues.add.issuecategory,
          PERMISSIONS.issues.change.issuecategory,
          PERMISSIONS.issues.delete.issuecategory,
        ],
        [translations.sms]: [PERMISSIONS.companies.change.company_sms_settings],
        [translations.terminal]: [
          PERMISSIONS.terminal.view.terminal,
          PERMISSIONS.terminal.change.terminal,
        ],
        [translations.userTypes]: [
          PERMISSIONS.users.view.usertype,
          PERMISSIONS.users.add.usertype,
          PERMISSIONS.users.change.usertype,
          PERMISSIONS.users.delete.usertype,
        ],
        [translations.userDocuments]: [
          PERMISSIONS.users.view.userdocument,
          PERMISSIONS.users.add.userdocument,
          PERMISSIONS.users.change.userdocument,
          PERMISSIONS.users.delete.userdocument,
        ],
        [translations.dropdowns]: [
          PERMISSIONS.users.add.dropdown,
          PERMISSIONS.users.change.dropdown,
        ],
        [translations.tableSettings]: [
          PERMISSIONS.dynamic_tables.view.dynamiccolumn,
          PERMISSIONS.dynamic_tables.change.dynamiccolumn,
        ],
      },
    },
  ]
}
